import type { HidChannel } from '../../../channels/hid/HidChannel'
import { CH315Config } from '../../../chips/wch-ch315/feature-impls/config'
import { CH315Firmware } from '../../../chips/wch-ch315/feature-impls/firmware'
import { CH315KeyboardKeys } from '../../../chips/wch-ch315/feature-impls/keyboard-keys'
import { CH315KeyboardLighting } from '../../../chips/wch-ch315/feature-impls/keyboard-lighting'
import { CH315KeyboardPerfCfg } from '../../../chips/wch-ch315/feature-impls/keyboard-performance'
import { CH315KeyTravel } from '../../../chips/wch-ch315/feature-impls/key-travel'
import {
  CH315KeyboardProtocol,
  type CH315IncomingReport,
  type CH315ProtocolOptions,
  type CH315UserStatusReport,
} from '../../../chips/wch-ch315/protocols'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import type { ch315DeviceCustom } from '../../../chips/wch-ch315/types/feature-types'
import { NoticeEvents } from '../../../events/NoticeEvents'
import { ReportEvents } from '../../../events/ReportEvents'
import { BaseDevice } from '../../../runtime/BaseDevice'
import type { DeviceManifest } from '../../../types/manifest'

/** CH315 键盘装配入口；设备生命周期内只创建一个协议客户端。 */
export class CH315Device extends BaseDevice {
  public readonly sdkVersion = '0.0.1'
  public readonly protocol: CH315KeyboardProtocol
  private readonly unsubscribeProtocol: () => void
  private closed = false

  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)
    this.protocol = new CH315KeyboardProtocol(channel, getCH315ProtocolOptions(manifest))
    this.unsubscribeProtocol = this.protocol.onInputReport((report) =>
      this.handleProtocolReport(report),
    )

    this.features.config = new CH315Config(this, this.protocol)
    this.features.keyboardPerfCfg = new CH315KeyboardPerfCfg(this, this.protocol)
    this.features.keyTravel = new CH315KeyTravel(this, this.protocol)
    this.features.keyboardLighting = new CH315KeyboardLighting(this, this.protocol)
    this.features.keyboardKeys = new CH315KeyboardKeys(this, this.protocol)
    this.features.firmware = new CH315Firmware(this, this.protocol)
  }

  override async open(): Promise<void> {
    if (this.closed) {
      throw new DeviceError(
        DeviceErrorCode.NOT_CONNECTED,
        'A closed CH315Device cannot be reopened; create a new device session',
      )
    }
    await this.channel.open()
    try {
      this.protocol.startListening()
      const config = this.features.config
      if (!config) throw new Error('CH315 config feature is not mounted')
      await config.getCurrentConfig()
      this.emit('connected')
    } catch (error) {
      await this.channel.close()
      throw error
    }
  }

  override async close(): Promise<void> {
    if (this.closed) return
    this.closed = true
    this.unsubscribeProtocol()
    this.protocol.dispose()
    await super.close()
  }

  async checkConnect(): Promise<boolean> {
    if (!this.channel.isConnected) return false
    try {
      await this.protocol.getFirmwareInfo()
      return true
    } catch {
      return false
    }
  }

  private handleProtocolReport(report: CH315IncomingReport): void {
    if (report.type === 'user-status') {
      this.forwardUserStatus(report.status)
    } else if (report.type === 'calibration') {
      this.emit(ReportEvents.KeyCalibUpload, {
        packetSequence: report.packetSequence,
        releaseFlag: report.releaseFlag,
        keys: report.keys,
      })
    }
  }

  private forwardUserStatus(status: CH315UserStatusReport): void {
    const userData: ch315DeviceCustom = {
      conProtocol: status.connectionMode,
      cfgSel: status.configIndex,
      blMode: status.backlightMode,
      blLevel: status.backlightLevel,
      blSpeed: status.backlightSpeed,
      blColor: status.backlightColor,
      sleepTime: status.sleepMinutes,
      rpt: status.reportRate,
      batPercent: status.batteryPercent,
      batStatus: ((status.functionWord >>> 14) & 0x03) as 0 | 1 | 2 | 3,
      functionWord: status.functionWord,
      usbPid: status.usbPid,
      deviceStatus: status.deviceStatus,
      rfConnected: ((status.connectionRssi >>> 7) & 1) as 0 | 1,
      rfRssi: status.connectionRssi & 0x7f,
      sideLightMode: status.sideLightMode,
      sideLightLevel: status.sideLightLevel,
      sideLightSpeed: status.sideLightSpeed,
      sideLightColor: status.sideLightColor,
    }

    const previous = this.userCustomData as ch315DeviceCustom | undefined
    this.emit(ReportEvents.KeyUserData, userData)

    if (
      userData.cfgSel >= 0 &&
      userData.cfgSel < 4 &&
      this.currentConfig.index !== userData.cfgSel
    ) {
      const known = this.configList.find((config) => config.index === userData.cfgSel)
      this.currentConfig = known ?? {
        index: userData.cfgSel,
        name: `Config ${userData.cfgSel + 1}`,
      }
      this.emit(ReportEvents.CfgChanged, userData)
      this.emit(NoticeEvents.CfgChanged, this.currentConfig)
    }
    if (
      previous?.batPercent !== userData.batPercent ||
      previous?.batStatus !== userData.batStatus
    ) {
      this.emit(ReportEvents.Battery, {
        batPercent: userData.batPercent,
        batStatus: userData.batStatus,
      })
    }
    if (previous?.blLevel !== userData.blLevel) {
      this.emit(ReportEvents.BrightnessChanged, userData.blLevel)
    }
    if (previous?.blSpeed !== userData.blSpeed) {
      this.emit(ReportEvents.SpeedChanged, userData.blSpeed)
    }
    this.userCustomData = userData
  }
}

function getCH315ProtocolOptions(manifest: DeviceManifest): CH315ProtocolOptions {
  return {
    normalReadLengthBytes: readLengthOption(
      manifest.driverOptions?.ch315NormalReadLengthBytes,
      'ch315NormalReadLengthBytes',
    ),
    rfReadLengthBytes: readLengthOption(
      manifest.driverOptions?.ch315RfReadLengthBytes,
      'ch315RfReadLengthBytes',
    ),
  }
}

function readLengthOption(value: unknown, name: string): 1 | 2 {
  if (value === undefined) return 2
  if (value === 1 || value === 2) return value
  throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${name} must be 1 or 2`)
}
